import type { ComponentType } from 'react';
import { useCallback, useEffect, useMemo } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import ArrowLeftIcon from '@atlaskit/icon/core/arrow-left';
import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';
import CheckIcon from '@atlaskit/icon/core/check-mark';
import { media } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { wacToken } from '@/libraries/design/foundations';
import { CustomerServiceManagementMarkBrand } from '@/libraries/wpl/foundations/src/logos/customerservicemanagement-mark-brand';
import { JiraServiceManagementMarkBrandV3 } from '@/libraries/wpl/foundations/src/logos-v3/jira-service-management-v3-mark-brand';

import ErrorBoundary from '../../components/percieved-provisioning/error-boundary/error-boundary';
import { useConfirmationPage } from '../../hooks/use-confirmation-page';
import { useScreenManager } from '../../hooks/use-screen-manager';
import { JsmUseCaseOnboardingContainer } from '../jsm-use-case-onboarding/jsm-use-case-onboarding-container';
import {
	TRACKING_EVENT_ACTION_SUBJECT_IDS,
	USER_SEARCH_PARAMS,
} from '../user-seg-bxp-ui/constants';

import {
	AnalyticsServiceManagementArtwork,
	CustomerServiceManagementArtwork,
	DesignServiceManagementArtwork,
	DevelopmentRequestArtwork,
	FinanceServiceManagementArtwork,
	GeneralServiceManagementArtwork,
	HrServiceManagementArtwork,
	IncidentManagementArtwork,
	LegalServiceManagementArtwork,
	MarketingServiceManagementArtwork,
	SalesServiceManagementArtwork,
	ServiceRequestManagementArtwork,
} from './jsm-use-case-template-choice-artworks';
import {
	JSM_USE_CASE_DEFAULT_TEAM_TYPE,
	JSM_TEMPLATE_ID_TO_BLUEPRINT_KEY,
	JSM_USE_CASE_TEMPLATE_OPTIONS_BY_TEAM_TYPE,
} from './jsm-use-case-template-choice-constants';
import { jsmUseCaseTemplateChoiceMessages } from './jsm-use-case-template-choice-messages';

import type {
	JsmUseCaseTemplateCardKey,
	JsmUseCaseTemplateOption,
} from './jsm-use-case-template-choice-constants';
import type { MessageDescriptor } from 'react-intl';

type TemplateCardContent = {
	readonly title: MessageDescriptor;
	readonly benefits: readonly MessageDescriptor[];
	readonly Artwork: ComponentType;
	readonly ProductMark: ComponentType;
};

const templateCardContentByKey: Record<JsmUseCaseTemplateCardKey, TemplateCardContent> = {
	'analytics-service-management': {
		title: jsmUseCaseTemplateChoiceMessages.analyticsServiceManagementTitle,
		benefits: [
			jsmUseCaseTemplateChoiceMessages.analyticsServiceManagementBenefitOne,
			jsmUseCaseTemplateChoiceMessages.analyticsServiceManagementBenefitTwo,
			jsmUseCaseTemplateChoiceMessages.analyticsServiceManagementBenefitThree,
		],
		Artwork: AnalyticsServiceManagementArtwork,
		ProductMark: JiraServiceManagementMarkBrandV3,
	},
	'customer-service-management': {
		title: jsmUseCaseTemplateChoiceMessages.customerServiceManagementTitle,
		benefits: [
			jsmUseCaseTemplateChoiceMessages.customerServiceManagementBenefitOne,
			jsmUseCaseTemplateChoiceMessages.customerServiceManagementBenefitTwo,
			jsmUseCaseTemplateChoiceMessages.customerServiceManagementBenefitThree,
		],
		Artwork: CustomerServiceManagementArtwork,
		ProductMark: CustomerServiceManagementMarkBrand,
	},
	'design-service-management': {
		title: jsmUseCaseTemplateChoiceMessages.designServiceManagementTitle,
		benefits: [
			jsmUseCaseTemplateChoiceMessages.designServiceManagementBenefitOne,
			jsmUseCaseTemplateChoiceMessages.designServiceManagementBenefitTwo,
			jsmUseCaseTemplateChoiceMessages.designServiceManagementBenefitThree,
		],
		Artwork: DesignServiceManagementArtwork,
		ProductMark: JiraServiceManagementMarkBrandV3,
	},
	'development-request': {
		title: jsmUseCaseTemplateChoiceMessages.developmentRequestTitle,
		benefits: [
			jsmUseCaseTemplateChoiceMessages.developmentRequestBenefitOne,
			jsmUseCaseTemplateChoiceMessages.developmentRequestBenefitTwo,
			jsmUseCaseTemplateChoiceMessages.developmentRequestBenefitThree,
		],
		Artwork: DevelopmentRequestArtwork,
		ProductMark: JiraServiceManagementMarkBrandV3,
	},
	'finance-service-management': {
		title: jsmUseCaseTemplateChoiceMessages.financeServiceManagementTitle,
		benefits: [
			jsmUseCaseTemplateChoiceMessages.financeServiceManagementBenefitOne,
			jsmUseCaseTemplateChoiceMessages.financeServiceManagementBenefitTwo,
			jsmUseCaseTemplateChoiceMessages.financeServiceManagementBenefitThree,
		],
		Artwork: FinanceServiceManagementArtwork,
		ProductMark: JiraServiceManagementMarkBrandV3,
	},
	'general-service-management': {
		title: jsmUseCaseTemplateChoiceMessages.generalServiceManagementTitle,
		benefits: [
			jsmUseCaseTemplateChoiceMessages.generalServiceManagementBenefitOne,
			jsmUseCaseTemplateChoiceMessages.generalServiceManagementBenefitTwo,
			jsmUseCaseTemplateChoiceMessages.generalServiceManagementBenefitThree,
		],
		Artwork: GeneralServiceManagementArtwork,
		ProductMark: JiraServiceManagementMarkBrandV3,
	},
	'hr-service-management': {
		title: jsmUseCaseTemplateChoiceMessages.hrServiceManagementTitle,
		benefits: [
			jsmUseCaseTemplateChoiceMessages.hrServiceManagementBenefitOne,
			jsmUseCaseTemplateChoiceMessages.hrServiceManagementBenefitTwo,
			jsmUseCaseTemplateChoiceMessages.hrServiceManagementBenefitThree,
		],
		Artwork: HrServiceManagementArtwork,
		ProductMark: JiraServiceManagementMarkBrandV3,
	},
	'incident-management': {
		title: jsmUseCaseTemplateChoiceMessages.incidentManagementTitle,
		benefits: [
			jsmUseCaseTemplateChoiceMessages.incidentManagementBenefitOne,
			jsmUseCaseTemplateChoiceMessages.incidentManagementBenefitTwo,
			jsmUseCaseTemplateChoiceMessages.incidentManagementBenefitThree,
		],
		Artwork: IncidentManagementArtwork,
		ProductMark: JiraServiceManagementMarkBrandV3,
	},
	'legal-service-management': {
		title: jsmUseCaseTemplateChoiceMessages.legalServiceManagementTitle,
		benefits: [
			jsmUseCaseTemplateChoiceMessages.legalServiceManagementBenefitOne,
			jsmUseCaseTemplateChoiceMessages.legalServiceManagementBenefitTwo,
			jsmUseCaseTemplateChoiceMessages.legalServiceManagementBenefitThree,
		],
		Artwork: LegalServiceManagementArtwork,
		ProductMark: JiraServiceManagementMarkBrandV3,
	},
	'marketing-service-management': {
		title: jsmUseCaseTemplateChoiceMessages.marketingServiceManagementTitle,
		benefits: [
			jsmUseCaseTemplateChoiceMessages.marketingServiceManagementBenefitOne,
			jsmUseCaseTemplateChoiceMessages.marketingServiceManagementBenefitTwo,
			jsmUseCaseTemplateChoiceMessages.marketingServiceManagementBenefitThree,
		],
		Artwork: MarketingServiceManagementArtwork,
		ProductMark: JiraServiceManagementMarkBrandV3,
	},
	'sales-service-management': {
		title: jsmUseCaseTemplateChoiceMessages.salesServiceManagementTitle,
		benefits: [
			jsmUseCaseTemplateChoiceMessages.salesServiceManagementBenefitOne,
			jsmUseCaseTemplateChoiceMessages.salesServiceManagementBenefitTwo,
			jsmUseCaseTemplateChoiceMessages.salesServiceManagementBenefitThree,
		],
		Artwork: SalesServiceManagementArtwork,
		ProductMark: JiraServiceManagementMarkBrandV3,
	},
	'service-request-management': {
		title: jsmUseCaseTemplateChoiceMessages.serviceRequestManagementTitle,
		benefits: [
			jsmUseCaseTemplateChoiceMessages.serviceRequestManagementBenefitOne,
			jsmUseCaseTemplateChoiceMessages.serviceRequestManagementBenefitTwo,
			jsmUseCaseTemplateChoiceMessages.serviceRequestManagementBenefitThree,
		],
		Artwork: ServiceRequestManagementArtwork,
		ProductMark: JiraServiceManagementMarkBrandV3,
	},
};

const removeUrlParameter = (url: string, paramKey: string) => {
	const updatedUrl = new URL(url);
	updatedUrl.searchParams.delete(paramKey);
	return updatedUrl.href;
};

const getTemplateOptionsDisplayOrder = (templateOptions: readonly JsmUseCaseTemplateOption[]) => {
	if (templateOptions.length !== 3) {
		return templateOptions;
	}

	return [...templateOptions].reverse();
};

export const JsmUseCaseTemplateChoiceScreen = () => {
	const intl = useIntl();
	const { nextScreen, prevScreen } = useScreenManager();
	const {
		userSegmentationContext: { selectedTeamType, setSelectedTeamType, selectTemplateOption },
	} = useConfirmationPage();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const templateOptions = useMemo(
		() =>
			JSM_USE_CASE_TEMPLATE_OPTIONS_BY_TEAM_TYPE[
				selectedTeamType ?? JSM_USE_CASE_DEFAULT_TEAM_TYPE
			],
		[selectedTeamType],
	);
	const displayTemplateOptions = useMemo(
		() => getTemplateOptionsDisplayOrder(templateOptions),
		[templateOptions],
	);

	useEffect(() => {
		createAnalyticsEvent({
			eventType: 'screen',
			name: TRACKING_EVENT_ACTION_SUBJECT_IDS.jsmUseCaseTemplateChoiceScreen,
			attributes: {
				selectedTeamType: selectedTeamType ?? JSM_USE_CASE_DEFAULT_TEAM_TYPE,
				templatesShown: displayTemplateOptions.map((t) => t.templateId),
				preSelectedTemplate:
					displayTemplateOptions.find((t) => t.isRecommended)?.templateId ?? null,
				isJSMOnboarding: true,
				isServiceCollectionEnabled: true,
			},
		}).fire();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleBack = () => {
		createAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: TRACKING_EVENT_ACTION_SUBJECT_IDS.jsmUseCaseTemplateBackClicked,
			attributes: {
				previouslySelectedTeamType: selectedTeamType ?? JSM_USE_CASE_DEFAULT_TEAM_TYPE,
				isJSMOnboarding: true,
				isServiceCollectionEnabled: true,
			},
		}).fire();
		selectTemplateOption(undefined);
		setSelectedTeamType(undefined);
		window.history.pushState(
			{},
			'',
			removeUrlParameter(window.location.href, USER_SEARCH_PARAMS.teamType),
		);
		prevScreen();
	};

	const handleSelectTemplate = (option: JsmUseCaseTemplateOption) => {
		const { templateId, isRecommended = false } = option;
		const templateKey = JSM_TEMPLATE_ID_TO_BLUEPRINT_KEY[templateId] ?? templateId;

		createAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: TRACKING_EVENT_ACTION_SUBJECT_IDS.jsmUseCaseTemplateSelected,
			attributes: {
				selectedTeamType: selectedTeamType ?? JSM_USE_CASE_DEFAULT_TEAM_TYPE,
				templateKey,
				isPreSelectedTemplate: isRecommended,
				isJSMOnboarding: true,
				isServiceCollectionEnabled: true,
			},
		}).fire();
		selectTemplateOption(option);
		nextScreen();
	};

	const handleError = useCallback(() => {
		createAnalyticsEvent({
			eventType: 'ui',
			actionSubject: 'jsmUseCaseTemplateChoiceScreen',
			action: 'failed',
			attributes: {
				isJSMOnboarding: true,
				isServiceCollectionEnabled: true,
			},
		}).fire();
	}, [createAnalyticsEvent]);

	const backButton = (
		<Button appearance="subtle" iconBefore={<ArrowLeftIcon label="" />} onClick={handleBack}>
			{intl.formatMessage(jsmUseCaseTemplateChoiceMessages.backButton)}
		</Button>
	);

	return (
		<ErrorBoundary onError={handleError}>
			<JsmUseCaseOnboardingContainer
				header={intl.formatMessage(jsmUseCaseTemplateChoiceMessages.header)}
				subheader={intl.formatMessage(jsmUseCaseTemplateChoiceMessages.subheader)}
				topLeftAction={backButton}
			>
				<div css={templateChoiceContainerStyles}>
					<div css={templateCardsRowStyles}>
						{displayTemplateOptions.map((templateOption) => (
							<TemplateChoiceCard
								key={templateOption.cardKey}
								templateOption={templateOption}
								onSelect={handleSelectTemplate}
							/>
						))}
					</div>
				</div>
			</JsmUseCaseOnboardingContainer>
		</ErrorBoundary>
	);
};

type TemplateChoiceCardProps = {
	readonly templateOption: JsmUseCaseTemplateOption;
	readonly onSelect: (option: JsmUseCaseTemplateOption) => void;
};

const TemplateChoiceCard = ({ templateOption, onSelect }: TemplateChoiceCardProps) => {
	const intl = useIntl();
	const content = templateCardContentByKey[templateOption.cardKey];
	const title = intl.formatMessage(content.title);

	return (
		<article
			css={[templateCardStyles, templateOption.isRecommended && recommendedTemplateCardStyles]}
		>
			<div css={productMarkWrapperStyles} aria-hidden="true">
				<span css={productMarkScaleStyles}>
					<content.ProductMark />
				</span>
			</div>
			<div css={artworkStyles}>
				<content.Artwork />
			</div>
			<h2 css={templateTitleStyles}>{title}</h2>
			{templateOption.isRecommended && (
				<span css={recommendedBadgeStyles}>
					{intl.formatMessage(jsmUseCaseTemplateChoiceMessages.recommended)}
				</span>
			)}
			<ul css={benefitsListStyles}>
				{content.benefits.map((benefit) => (
					<li key={benefit.id} css={benefitItemStyles}>
						<CheckIcon label="" color="currentColor" />
						<span>{intl.formatMessage(benefit)}</span>
					</li>
				))}
			</ul>
			<div css={selectButtonWrapperStyles}>
				<Button
					appearance="default"
					css={selectButtonStyles}
					onClick={() => onSelect(templateOption)}
					aria-label={intl.formatMessage(jsmUseCaseTemplateChoiceMessages.selectTemplateButton, {
						templateName: title,
					})}
					shouldFitContainer
				>
					<span css={selectButtonContentStyles}>
						{intl.formatMessage(jsmUseCaseTemplateChoiceMessages.selectButton)}
						<ArrowRightIcon label="" />
					</span>
				</Button>
			</div>
		</article>
	);
};

const templateChoiceContainerStyles = css({
	width: '100%',
	maxWidth: '795px',
	marginInlineStart: 'auto',
	marginInlineEnd: 'auto',
});

const templateCardsRowStyles = css({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'stretch',
	rowGap: token('space.300'),
	[media.above.md]: {
		flexDirection: 'row',
		justifyContent: 'center',
		alignItems: 'center',
		columnGap: token('space.300'),
	},
});

const templateCardStyles = css({
	position: 'relative',
	display: 'flex',
	flexDirection: 'column',
	width: '100%',
	minHeight: '250px',
	boxSizing: 'border-box',
	paddingBlockStart: token('space.200'),
	paddingBlockEnd: token('space.200'),
	paddingInlineStart: token('space.200'),
	paddingInlineEnd: token('space.200'),
	backgroundColor: token('elevation.surface'),
	border: `1px solid ${token('color.border')}`,
	borderRadius: token('radius.large', '8px'),
	[media.above.md]: {
		maxWidth: '249px',
	},
});

const recommendedTemplateCardStyles = css({
	order: -1,
	minHeight: '296px',
	borderColor: wacToken('color.extended.yellow200'),
	borderStyle: 'solid',
	borderWidth: token('border.width', '1px'),
	boxShadow: `0px 0px 8px ${wacToken('color.extended.saffron300')}59`,
	[media.above.md]: {
		order: 0,
	},
});

const productMarkWrapperStyles = css({
	width: '20px',
	height: '20px',
	display: 'inline-flex',
	flexShrink: 0,
	overflow: 'hidden',
	marginBlockEnd: token('space.100'),
});

const productMarkScaleStyles = css({
	display: 'inline-flex',
	transform: 'scale(0.4167)',
	transformOrigin: 'top left',
});

const artworkStyles = css({
	position: 'absolute',
	insetBlockStart: token('space.025'),
	insetInlineEnd: token('space.025'),
	width: '96px',
	height: '96px',
	overflow: 'visible',
	pointerEvents: 'none',
	[media.above.md]: {
		insetBlockStart: '-12px',
		insetInlineEnd: '-12px',
		width: '96px',
		height: '96px',
	},
});

const templateTitleStyles = css({
	position: 'relative',
	zIndex: 1,
	maxWidth: '164px',
	marginBlockStart: '0',
	marginBlockEnd: token('space.200'),
	color: token('color.text'),
	font: token('font.heading.medium'),
});

const recommendedBadgeStyles = css({
	alignSelf: 'flex-start',
	marginBlockEnd: token('space.200'),
	paddingBlockStart: token('space.025'),
	paddingBlockEnd: token('space.025'),
	paddingInlineStart: token('space.075'),
	paddingInlineEnd: token('space.075'),
	color: token('color.text'),
	backgroundColor: wacToken('color.extended.saffron300'),
	borderRadius: token('radius.small', '3px'),
	font: token('font.body.small'),
	fontWeight: token('font.weight.semibold'),
});

const benefitsListStyles = css({
	display: 'flex',
	flexDirection: 'column',
	rowGap: token('space.100'),
	marginBlockStart: '0',
	marginBlockEnd: token('space.300'),
	paddingInlineStart: '0',
	listStyle: 'none',
	color: token('color.text.subtle'),
	font: token('font.body.small'),
});

const benefitItemStyles = css({
	display: 'flex',
	alignItems: 'flex-start',
	columnGap: token('space.075'),
});

const selectButtonWrapperStyles = css({
	marginBlockStart: 'auto',
});

const selectButtonContentStyles = css({
	display: 'inline-flex',
	alignItems: 'center',
	columnGap: token('space.050'),
});

const selectButtonStyles = css({
	borderRadius: token('radius.small', '3px'),
	':not(:hover):not(:active)': {
		backgroundColor: token('elevation.surface'),
		borderColor: token('color.border'),
		borderStyle: 'solid',
		borderWidth: token('border.width', '1px'),
	},
});
